/**
 * The **blood-splatter scatter roll** (Blood splatter, PRD #372, Slice B): the
 * pure, RNG-injected core that decides *where* and *which art* each splat of a
 * landed hit lands, so the server rolls one scatter and broadcasts it to every
 * viewer (the {@link ../net/protocol.BloodFxMessage}) rather than each client
 * inventing its own. Kept Pixi-free and in `sim/` so it is the single wire-safe
 * home of the {@link SplatRoll} shape - the render layer ({@link
 * ../render/bloodLayer}) re-exports it, and the protocol imports it here rather
 * than reaching into a render module.
 *
 * It reads no wall clock and no `Math.random` (the architecture contract): the
 * randomness enters only as the injected `rng` (the server's `bloodRng` seam, the
 * `duelRng`/`diceRng` precedent), so a test pins an exact splat layout.
 */
import { BLOOD_ART_COUNT } from './content';

/**
 * One rolled splat: a tile offset within the 3x3 scatter box (Chebyshev 1) plus
 * the blood art index it draws. Structurally identical to the render layer's
 * `SplatRoll` (which re-exports this), so a decoded {@link
 * ../net/protocol.BloodFxMessage} feeds straight into `BloodLayer.splat`.
 */
export interface SplatRoll {
  /** Tile offset from the victim tile, in `{-1, 0, 1}` (the 3x3 box). */
  dx: number;
  dy: number;
  /** Blood art index `0..BLOOD_ART_COUNT-1` (which of the 7 blood statics). */
  art: number;
}

/**
 * How many splats a landed hit scatters, scaling with lethality (PRD #372): **1**
 * for an ordinary hit, **2** if the hit takes the victim below half of its maximum
 * health, **3** on a kill (health at or below zero). Evaluated on the victim's
 * **post-damage** health, so the caller must apply the damage first. A victim with
 * no `max` (defensive) reads as an ordinary hit.
 */
export function bloodSplatCount(current: number, max: number): number {
  if (current <= 0) return 3;
  if (max > 0 && current < max / 2) return 2;
  return 1;
}

/**
 * Roll `count` splats off the injected `rng`, each an independent offset in the
 * 3x3 box (`dx, dy` in `{-1, 0, 1}`) and art index in `[0, BLOOD_ART_COUNT)`. The
 * three draws per splat happen in a fixed order - `dx`, then `dy`, then `art` - so
 * a scripted `rng` produces a deterministic, pinnable layout. Pure over its args.
 */
export function rollBloodScatter(count: number, rng: () => number): SplatRoll[] {
  const splats: SplatRoll[] = [];
  for (let i = 0; i < count; i++) {
    splats.push({
      dx: Math.floor(rng() * 3) - 1,
      dy: Math.floor(rng() * 3) - 1,
      art: Math.floor(rng() * BLOOD_ART_COUNT),
    });
  }
  return splats;
}
